// src/components/EmailCaptureForm.jsx - MATCHES CSS FILES
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import '../styles/email-capture-form.css';

/**
 * EmailCaptureForm
 * Captures user email for follow-up contact and counselor connection
 */
const EmailCaptureForm = ({ sessionId, tenantId, firstName }) => {
  const [email, setEmail] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Validate email
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      setSubmitting(false);
      return;
    }

    try {
      const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(
        `${API_ROOT}/api/sessions/${sessionId}/email`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            emailOptIn,
            tenantId
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save email');
      }

      const data = await response.json();
      setSubmitted(true);
      console.log('Email captured:', data);
    } catch (err) {
      console.error('Email capture error:', err);
      setError(err.message || 'Unable to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div className="mwc-card email-capture-success">
        <div className="email-capture-success__icon">✅</div>
        <h2 className="email-capture-success__title">
          Thank You{firstName ? `, ${firstName}` : ''}!
        </h2>
        <p className="email-capture-success__message">
          We've received your information and a counselor will be in touch at <strong>{email}</strong>
        </p>
        {emailOptIn && (
          <p className="email-capture-success__counselor">
            You'll also receive updates about programs and opportunities that match your interests!
          </p>
        )}
        <p className="email-capture-success__subtitle" style={{ marginTop: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
          In the meantime, explore the programs and resources we've matched for you below.
        </p>
      </div>
    );
  }

  // Form state
  return (
    <div className="mwc-card email-capture-card">
      <div className="email-capture-header">
        <h2 className="email-capture-title">
          💬 Want to Learn More?
        </h2>
        <p className="email-capture-subtitle">
          {firstName
            ? `${firstName}, if you'd like a counselor to reach out with more information about these programs, leave your email below.`
            : `If you'd like a counselor to reach out with more information about these programs, leave your email below.`
          }
        </p>
      </div>

      <form onSubmit={handleSubmit} className="email-capture-form">
        {/* Email input */}
        <div className="email-capture-input-group">
          <input
            id="email"
            type="email"
            className="email-capture-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            required
            disabled={submitting}
          />
          <button
            type="submit"
            className="mwc-btn mwc-btn--primary"
            disabled={submitting || !email}
          >
            {submitting ? 'Sending...' : 'Contact Me'}
          </button>
        </div>

        {/* Opt-in checkbox */}
        <label className="email-capture-checkbox">
          <input
            type="checkbox"
            checked={emailOptIn}
            onChange={(e) => setEmailOptIn(e.target.checked)}
            disabled={submitting}
          />
          <span className="email-capture-checkbox-label">
            I'd also like to receive updates about programs and opportunities
          </span>
        </label>

        {/* Error message */}
        {error && (
          <div className="email-capture-error" role="alert">
            {error}
          </div>
        )}

        {/* Privacy note */}
        <p className="email-capture-privacy">
          We respect your privacy. Your email will only be used to connect you with a counselor and send relevant program information.
        </p>
      </form>
    </div>
  );
};

EmailCaptureForm.propTypes = {
  sessionId: PropTypes.string.isRequired,
  tenantId: PropTypes.string.isRequired,
  firstName: PropTypes.string
};

export default EmailCaptureForm;